/**
 * Maps `GET|PUT /admin-panel-v2/configurations/profile-shortcuts` wire DTOs
 * onto the `ProfileShortcut` view-model that `ShortcutsTable`/`ShortcutRow`
 * already render (and the reverse for the PUT full-list replace). Isolating
 * the translation here means a backend field rename only requires a change
 * in this file — no component under
 * `src/components/Configurations/profile-configuration/shortcuts/` needs to
 * be touched.
 */
import type {
  ProfileShortcut,
  ProfileShortcutDto,
  ProfileShortcutWriteDto,
} from "../../types/profileShortcuts";

export function mapProfileShortcutDto(dto: ProfileShortcutDto): ProfileShortcut {
  return {
    id: dto.id,
    name: dto.name,
    icon: dto.icon?.trim() ? dto.icon : null,
    order: Number(dto.order),
    isLive: dto.enabled,
    // Wire does not yet expose is_default — treat API-seeded rows as defaults.
    // Client-created rows set isDefault: false in FeatureQuickAccess and that
    // flag is preserved across Save Changes via the local session map.
    isDefault: true,
  };
}

/**
 * Maps the full shortcuts array and sorts it ascending by `order`, since
 * `ShortcutsTable` requires an already-sorted list and the API makes no
 * guarantee about response ordering.
 */
export function mapProfileShortcuts(
  dtos: ProfileShortcutDto[] | null | undefined,
): ProfileShortcut[] {
  if (!dtos?.length) return [];
  return dtos.map(mapProfileShortcutDto).sort((a, b) => a.order - b.order);
}

/** Maps a UI shortcut onto the PUT write DTO (full-list replace). */
export function mapProfileShortcutToWriteDto(
  shortcut: ProfileShortcut,
): ProfileShortcutWriteDto {
  return {
    id: shortcut.id,
    name: shortcut.name,
    icon: shortcut.icon ?? "",
    enabled: shortcut.isLive,
    order: shortcut.order,
  };
}

/** Maps the full UI list onto the PUT `shortcuts` array. */
export function mapProfileShortcutsToWriteDtos(
  shortcuts: ProfileShortcut[],
): ProfileShortcutWriteDto[] {
  return shortcuts.map(mapProfileShortcutToWriteDto);
}
